// @coverage-include-exempt: thin hook over useExperiment (a latch plus a
// timer), the same shape as use-ad-wall-arm.ts. Its behaviour (light
// fallback, payload config, no late flip) is asserted through
// tests/component/monetization/ad-wall-host.test.tsx; the payload rules
// through tests/unit/lib/monetization/ad-wall.test.ts.
"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { useExperiment } from "@/lib/analytics/use-experiment";
import { CLIENT_RESOLVE_TIMEOUT_MS } from "@/lib/net/fetch-with-timeout";
import {
	AD_WALL_INTENSITIES,
	AD_WALL_INTENSITY_DEFAULTS,
	AD_WALL_INTENSITY_FLAG,
	type AdWallIntensity,
	type AdWallIntensityConfig,
	resolveIntensityConfig,
	toAdWallIntensity,
} from "./ad-wall";

const VARIANTS = Object.fromEntries(
	AD_WALL_INTENSITIES.map((arm) => [arm, arm]),
) as Record<AdWallIntensity, AdWallIntensity>;

type Decided = { intensity: AdWallIntensity; config: AdWallIntensityConfig };

const LIGHT: Decided = {
	intensity: "light",
	config: AD_WALL_INTENSITY_DEFAULTS.light,
};

/**
 * The visitor's H8 `ad-wall-intensity` arm and its settings (the variant's
 * flag payload over the arm defaults). Flag absent, PostHog blocked, or
 * unresolved after `CLIENT_RESOLVE_TIMEOUT_MS` decide `light` (H7's wall),
 * and the decision LATCHES so a late flag never changes a wall mid-session.
 */
export function useAdWallIntensity(): {
	ready: boolean;
	intensity: AdWallIntensity;
	config: AdWallIntensityConfig;
} {
	const posthog = usePostHog();
	// Exposure is the host's `ad_wall_eligible`, which carries `intensity`.
	const { variantKey, isReady } = useExperiment<AdWallIntensity>({
		flagKey: AD_WALL_INTENSITY_FLAG,
		variants: VARIANTS,
		trackExposure: false,
	});
	const [decided, setDecided] = useState<Decided | null>(null);

	useEffect(() => {
		if (!isReady) return;
		setDecided((prev) => {
			if (prev) return prev;
			const intensity = toAdWallIntensity(variantKey);
			let payload: unknown = null;
			try {
				payload = posthog?.getFeatureFlagPayload?.(AD_WALL_INTENSITY_FLAG);
			} catch {
				payload = null;
			}
			return { intensity, config: resolveIntensityConfig(intensity, payload) };
		});
	}, [isReady, variantKey, posthog]);

	useEffect(() => {
		const timer = setTimeout(
			() => setDecided((prev) => prev ?? LIGHT),
			CLIENT_RESOLVE_TIMEOUT_MS,
		);
		return () => clearTimeout(timer);
	}, []);

	const d = decided ?? LIGHT;
	return { ready: decided !== null, intensity: d.intensity, config: d.config };
}
